"use client"

import { Suspense, useState, type ComponentProps } from "react"
import { usePathname } from "next/navigation"
import clsx from "clsx"

import type { InferReturn, PropsWithClassName } from "~/@types/generics"
import { ALERT_CODES } from "~/lib/constants"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import { PRODUCT_CUSTOM_ATTRIBUTES, type ColorCustomAttribute } from "~/lib/shopify/constants"
import { useAddToCart } from "~/lib/shopify/hooks/useAddToCart"
import type { UnlikelyCartLineItemInput } from "~/lib/shopify/types"
import serializePrice from "~/data/price/serializer"
import useGetCart from "~/hooks/cart/useGetCart"
import { useAlerts } from "~/hooks/useAlerts"
import useBreakpoint from "~/hooks/useBreakpoint"
import { AddToCartButton } from "~/components/ui/AddToCartButton"
import { UNIVERSAL_COLOR_OPTION_VALUE } from "~/components/ui/Product/ProductHeader/_data/constants"
import type { TProductHeaderProduct } from "~/components/ui/Product/ProductHeader/_data/serializer"
import {
  getOptionsForSingleProduct,
  getOptionsWithoutColor,
  getOptionWithoutColor,
} from "~/components/ui/Product/ProductHeader/_data/utils/get-uniq-variants-without-color"
import { isProductHeaderWithEmbroidery } from "~/components/ui/Product/ProductHeader/_data/utils/is-product-header-with-embroidery"
import { ColorsSelector } from "~/components/ui/Product/ProductHeader/ColorsSelector"
import { PanelEmbroideryTrigger } from "~/components/ui/Product/ProductHeader/PanelEmbroideryTrigger"
import { Alma } from "~/components/ui/Product/ProductHeader/ProductDetails/Alma"
import { DeliveryEstimations } from "~/components/ui/Product/ProductHeader/ProductDetails/DeliveryEstimations"
import ProductFomo from "~/components/ui/Product/ProductHeader/ProductDetails/ProductSelector/ProductFomo"
import { ProductOptionsSelector } from "~/components/ui/Product/ProductHeader/ProductDetails/ProductSelector/ProductOptionsSelector"
import { ProductOptionsSelectSelector } from "~/components/ui/Product/ProductHeader/ProductDetails/ProductSelector/ProductOptionsSelectSelector"
import getVariantFromParams from "~/components/ui/Product/ProductHeader/ProductDetails/ProductSelector/utils/get-variant-from-params"
import { Stack } from "~/components/abstracts/Stack"
import { Cart } from "~/components/globals/Cart"
import { getCrossSellProductLineItem } from "~/components/globals/Cart/components/CrossSell/_data/serializeCrossSellProductCard"
import CrossSellProductCardCheckbox from "~/components/globals/Cart/components/CrossSell/CrossSellProductCardCheckbox"
import { useCartState } from "~/components/globals/Cart/useCartState"
import { ClientViewItem } from "~/providers/GTMTrackingProvider/ClientViewItem"
import { serializeItemIdFromProductInfos, type CartLineItemPayload } from "~/providers/GTMTrackingProvider/constants"
import { usePanel } from "~/managers/PanelManager"
import getAlternateUrl from "~/utils/alternate-url"
import type { useVariantSwitch } from "~/app/[locale]/products/[slug]/_hooks/use-variant-switch"

export type ProductSelectorProps = PropsWithClassName<{
  children: {
    sizeChartPanelTrigger: React.ReactNode
  }
  productHeader: TProductHeaderProduct
  currentProductMultipleOptions?: InferReturn<typeof useVariantSwitch>["currentProductMultipleOptions"]
  setCurrentProductMultipleOptions?: InferReturn<typeof useVariantSwitch>["setCurrentProductMultipleOptions"]
}>

export function ProductSelector({
  className,
  productHeader,
  children,
  currentProductMultipleOptions,
  setCurrentProductMultipleOptions,
}: ProductSelectorProps) {
  const { add } = usePanel()
  const { mutate: addCartLines, isLoading } = useAddToCart()
  const triggerAlert = useAlerts()
  const pathname = usePathname()
  const locale = useLocale()
  const { cart } = useGetCart()
  const isMobile = useBreakpoint("md")
  const setCurrentState = useCartState()[1]

  const [crossSellCtaElement, setCrossSellCtaElement] =
    useState<Parameters<NonNullable<ComponentProps<typeof CrossSellProductCardCheckbox>["onChange"]>>[0]>(null)

  const crossSellCtaLineItem =
    crossSellCtaElement && productHeader?.crossSellCta?.crossSellProductCard?.product
      ? getCrossSellProductLineItem({
          product: productHeader?.crossSellCta?.crossSellProductCard?.product,
          index: cart && cart.lines ? cart.lines.length + 1 : 0,
          pathname,
          additionalAttributes: [
            { key: PRODUCT_CUSTOM_ATTRIBUTES.IS_FROM_PRE_CTA_CROSS_SELL, value: "true" },
            {
              key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION_IDS,
              value: JSON.stringify(
                productHeader?.crossSellCta?.crossSellProductCard?.product?.collections.map(
                  (collection) => collection.id
                )
              ),
            },
          ],
          additionalTrackingData: { from_pre_cta_cross_sell: true },
          ...crossSellCtaElement,
        })
      : null

  const currentVariant =
    getVariantFromParams(productHeader?.variants, currentProductMultipleOptions) ?? productHeader?.variants?.[0] ?? null

  const defaultVariant = currentVariant ?? productHeader?.variants?.[0] ?? null
  const currentOption = defaultVariant ? getOptionWithoutColor(defaultVariant) : null

  const currentColorWithDotImage =
    productHeader.colorsWithDotImage &&
    productHeader?.colorsWithDotImage.find(
      (colorWithDotImage) => colorWithDotImage?.value === productHeader?.currentColor?.value
    )

  const dotColor = {
    image: currentColorWithDotImage?.image,
    value: currentColorWithDotImage?.value,
  } satisfies ColorCustomAttribute

  const productLineItem = {
    quantity: 1,
    merchandiseId: currentVariant?.id ? currentVariant.id : "",
    attributes: [
      { key: PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_ID, value: productHeader?.id ?? "" },
      { key: PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_COLOR, value: JSON.stringify(dotColor) },
      {
        key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION_IDS,
        value: JSON.stringify(productHeader?.collectionIds),
      },
      { key: PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_URL, value: getAlternateUrl(pathname) ?? "" },
      {
        key: PRODUCT_CUSTOM_ATTRIBUTES.TRACKING_DATA,
        value: JSON.stringify({
          item_variant: currentVariant?.title ?? "",
          item_id:
            productHeader?.id && currentVariant?.id
              ? serializeItemIdFromProductInfos(productHeader?.id, currentVariant?.id)
              : "",
          item_name: productHeader?.title ?? "",
          item_sku: currentVariant?.sku ?? "",
          item_url: getAlternateUrl(pathname) ?? "",
          item_image_url: currentVariant?.image?.[0]?.src ?? "",
          price: currentVariant?.unserializedPrice?.amount ?? null,
          compare_at_price: currentVariant?.unserializedCompareAtPrice?.amount ?? null,
          currency: currentVariant?.unserializedPrice?.currencyCode ?? "",
          quantity: 1,
          item_category: productHeader.collection ?? "",
          index: cart && cart.lines ? cart.lines.length : 0,
        } satisfies CartLineItemPayload),
      },
      // Used inside serializeCartPayload
      ...(productHeader.collection
        ? [{ key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION, value: productHeader.collection }]
        : []),
    ],
  } satisfies UnlikelyCartLineItemInput

  const onClick = () => {
    addCartLines([productLineItem, crossSellCtaLineItem].filter(Boolean), {
      onSuccess: ({ errors }) => {
        if (errors.length > 0) {
          triggerAlert(ALERT_CODES.OUT_OF_STOCK)
        } else {
          const withCrossSell = productHeader?.crossSellPopin?.length

          const cartProps = withCrossSell
            ? ({
                crossSellProductIds: productHeader.crossSellPopin,
                selectedOptions: currentVariant?.selectedOptions ?? [],
              } as const)
            : {}

          setCurrentState(withCrossSell ? "cross-sell" : isMobile ? "toaster" : "cart")
          add(<Cart {...cartProps} />)
        }
      },
    })
  }

  const optionsWithValues = (getOptionsWithoutColor(productHeader?.variants) ?? []).filter(
    (option) => option.value !== ""
  )

  const optionsWithValuesSelectSelector = getOptionsForSingleProduct(productHeader?.variants ?? [])

  const filteredColors = productHeader.colorsWithDotImage?.filter(
    (color) => color.image && color.value !== UNIVERSAL_COLOR_OPTION_VALUE
  )

  const hasConfortOption = optionsWithValuesSelectSelector?.every((option) => option.confort)

  const price = serializePrice({
    locale,
    amount:
      (crossSellCtaElement?.variant?.unserializedPrice?.amount ?? 0) + (currentVariant?.unserializedPrice?.amount ?? 0),
    currencyCode: currentVariant?.unserializedPrice?.currencyCode ?? "",
  })

  const crossSellCta = productHeader?.crossSellCta?.crossSellProductCard && (
    <CrossSellProductCardCheckbox
      item={productHeader?.crossSellCta?.crossSellProductCard}
      selectedOptions={currentVariant?.selectedOptions ?? undefined}
      customTitle={productHeader?.crossSellCta?.customTitle}
      customImage={productHeader?.crossSellCta?.customImage}
      theme="grey"
      size="small"
      onChange={setCrossSellCtaElement}
      selectorsOrder={["size"]}
    />
  )

  return productHeader?.variants && productHeader?.variants.length > 0 ? (
    <div className={clsx(className)} data-comp="UI/Product/ProductHeader/ProductDetails/ProductSelector">
      {filteredColors && filteredColors.length > 0 && (
        <Suspense>
          <ColorsSelector
            colorTag={productHeader?.colorTag}
            sprinklesCss={{ pB: 30, pX: 10 }}
            currentColor={dotColor?.value}
            colorsWithDotImage={filteredColors}
            key={dotColor?.value}
          />
        </Suspense>
      )}
      {optionsWithValues && optionsWithValues.length > 1 && !hasConfortOption && (
        <ProductOptionsSelector
          onChange={(option) => {
            if (option?.value) {
              setCurrentProductMultipleOptions?.(option?.value)
            }
          }}
          options={optionsWithValues}
          currentOption={currentOption}
          sizeChartPanel={children.sizeChartPanelTrigger}
        >
          {currentVariant?.id && (
            <Stack gap={12} direction="column" sprinklesCss={{ mB: 20, mX: { mobile: 10, desktop: 0 } }}>
              {productHeader.globalProduct?.fomo && productHeader.id && defaultVariant && (
                <ProductFomo
                  productId={productHeader.id}
                  currentVariant={defaultVariant}
                  {...productHeader.globalProduct?.fomo}
                />
              )}
              {isProductHeaderWithEmbroidery(productHeader) && (
                <PanelEmbroideryTrigger
                  currentVariant={currentVariant}
                  productHeader={productHeader}
                  productLineItem={productLineItem}
                />
              )}
              {crossSellCta}
              <AddToCartButton
                onClick={onClick}
                isLoading={isLoading}
                isOutOfStock={!currentVariant?.availableForSale}
                width="full"
                disabled={!price}
                price={price}
                variantId={currentVariant.id}
                popinBackInStock={productHeader?.popInBackInStock ?? null}
                productName={`${productHeader.title} - ${currentVariant?.title}`}
              />
              {productHeader.globalProduct?.almaContent && <Alma data={productHeader.globalProduct?.almaContent} />}
              <DeliveryEstimations />
            </Stack>
          )}
        </ProductOptionsSelector>
      )}
      {optionsWithValuesSelectSelector &&
        optionsWithValuesSelectSelector.length > 1 &&
        currentVariant &&
        hasConfortOption && (
          <ProductOptionsSelectSelector
            onChange={(option) => {
              if (option?.size && option?.confort) {
                setCurrentProductMultipleOptions?.(option.size, option.confort)
              }
            }}
            options={optionsWithValuesSelectSelector}
            currentVariant={currentVariant}
          >
            <Stack
              direction="column"
              gap={25}
              sprinklesCss={{
                mX: { mobile: 10, desktop: 0 },
                mT: 20,
              }}
            >
              {children?.sizeChartPanelTrigger ? children.sizeChartPanelTrigger : null}
              {currentVariant?.id && (
                <Stack gap={12} direction="column" sprinklesCss={{ mB: 20 }}>
                  {isProductHeaderWithEmbroidery(productHeader) && (
                    <PanelEmbroideryTrigger
                      currentVariant={currentVariant}
                      productHeader={productHeader}
                      productLineItem={productLineItem}
                    />
                  )}
                  {crossSellCta}
                  <AddToCartButton
                    onClick={onClick}
                    isLoading={isLoading}
                    isOutOfStock={!currentVariant?.availableForSale}
                    width="full"
                    disabled={!price}
                    price={price}
                    variantId={currentVariant.id}
                    popinBackInStock={productHeader?.popInBackInStock ?? null}
                    productName={`${productHeader.title} - ${currentVariant?.title}`}
                  />
                  {productHeader.globalProduct?.almaContent && <Alma data={productHeader.globalProduct?.almaContent} />}
                  <DeliveryEstimations />
                </Stack>
              )}
            </Stack>
          </ProductOptionsSelectSelector>
        )}
      {optionsWithValuesSelectSelector && optionsWithValuesSelectSelector.length === 1 && currentVariant?.id && (
        <Stack gap={12} direction="column" sprinklesCss={{ mB: 20, mX: { mobile: 10, desktop: 0 } }}>
          {isProductHeaderWithEmbroidery(productHeader) && (
            <PanelEmbroideryTrigger
              currentVariant={currentVariant}
              productHeader={productHeader}
              productLineItem={productLineItem}
            />
          )}
          {crossSellCta}
          <AddToCartButton
            onClick={onClick}
            isLoading={isLoading}
            isOutOfStock={!currentVariant?.availableForSale}
            width="full"
            disabled={!price}
            price={price}
            variantId={currentVariant.id}
            popinBackInStock={productHeader?.popInBackInStock ?? null}
            productName={`${productHeader.title} - ${currentVariant?.title}`}
          />
          {productHeader.globalProduct?.almaContent && <Alma data={productHeader.globalProduct?.almaContent} />}
          <DeliveryEstimations />
        </Stack>
      )}
      {currentVariant && (
        <ClientViewItem
          payload={{
            item_variant: currentVariant?.title ?? "",
            item_id: serializeItemIdFromProductInfos(productHeader?.id, currentVariant?.id),
            item_name: productHeader.title ?? "",
            item_sku: currentVariant.sku ?? "",
            item_url: getAlternateUrl(pathname) ?? "",
            item_image_url: currentVariant?.image?.[0]?.src ? `${currentVariant?.image?.[0]?.src}?width=800` : "",
            item_category: productHeader.collection ?? "",
            price: currentVariant?.unserializedPrice?.amount ?? 0,
            compare_at_price: currentVariant?.unserializedCompareAtPrice?.amount ?? 0,
            currency: productHeader.unserializedPrice?.currencyCode ?? "",
            quantity: 1,
          }}
          deps={[currentVariant?.id]}
        />
      )}
    </div>
  ) : null
}
